import React, { useState, useEffect } from "react";
import EkatraCircleLogo from "../components/EkatraCircleLogo";
import { useI18n } from "../i18n/I18nProvider";

export default function DialogueSolution() {
  const { t } = useI18n();

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const locale =
    typeof window !== "undefined"
      ? localStorage.getItem("app_locale") || "en"
      : "en";

  const isEnglish = locale === "en";

  return (
    <section className={`relative px-4 sm:px-6 pb-24 md:pb-12 bg-[#fdf7ef] ${isMobile ? "pt-2 md:pt-12" : "pt-16 md:pt-12"}`}>
      {/* ================= ORANGE CARD ================= */}
      <div className="relative max-w-6xl w-full mx-auto rounded-[24px] md:rounded-[28px] px-6 sm:px-8 md:px-10 overflow-hidden py-16 bg-[#F1AF38]">
        {/* ================= TOP CURVE - creates wavy top edge with 3 bumps ================= */}
        <div
          className="absolute top-0 left-0 w-full h-[100px] sm:h-[110px] md:h-[120px] pointer-events-none"
          style={{ marginBottom: "-1px" }}
        >
          <svg
            className="w-full h-full"
            viewBox="0 0 1240 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: "block" }}
          >
            {/* Background section color showing through the curve */}
            <rect width="1240" height="120" fill="#fdf7ef" />
            {/* Orange fill with curve - creates 3 bumps, beige shows in valleys - extended slightly to prevent white line */}
            <path
              d="M0 40C0 17.9086 17.9086 0 40 0H310H407.177C444.924 0 481.87 10.8839 513.589 31.3474C578.373 73.144 661.627 73.144 726.411 31.3474C758.13 10.8839 795.076 0 832.823 0H930H1200C1222.09 0 1240 17.9086 1240 40V125H0V40Z"
              fill="#F1AF38"
            />
          </svg>
        </div>

        {/* ================= BOTTOM CURVE - creates smooth convex arch from bottom corners ================= */}
        <div
          className="hidden sm:block absolute inset-x-0 bottom-0 h-[90px] pointer-events-none overflow-hidden"
          style={{ marginTop: "-1px" }}
        >
          <svg
            className="w-full h-full"
            viewBox="0 0 1440 90"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: "block" }}
          >
            {/* Beige background showing through the arch - extended slightly to prevent white line */}
            <path d="M0 95 Q720 -5 1440 95 L1440 95 L0 95 Z" fill="#fdf7ef" />
          </svg>
        </div>
        {/* ================= CONTENT ================= */}
        <div className="relative z-10">
          {/* Badge */}
          <div className="inline-block border border-[#033049] px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium text-[#033049] ">
            {t("dialogue.badge")}
          </div>

          {/* HEADER + LOGO */}
          <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-10">
            {/* LEFT TEXT */}
            <div className="max-w-xl sm:max-w-2xl">
              <h2 className="font-lexend text-[#033049] text-[32px] sm:text-[40px] md:text-[52px] lg:text-[60px] xl:text-[72px] leading-tight">
                {t("dialogue.title.prefix")}{" "}
                <span className="font-bold">{t("dialogue.title.brand")}</span>
                <br />
                {t("dialogue.title.dialogue")}{" "}
                <span className="font-bold">
                  {t("dialogue.title.solution")}
                </span>
              </h2>

              {/* UNDERLINE */}
              <svg
                className="mt-2 ml-0 sm:ml-auto block w-40 sm:w-56 md:w-[314px] h-auto"
                viewBox="0 0 314 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.00079 19.2104L46.8211 12.0796C60.9868 9.6642 75.546 11.271 88.844 16.7174C103.846 22.8615 120.407 24.1017 136.156 20.2605L175.278 10.7191C211.591 1.86263 249.654 3.31606 285.186 14.9158L309.001 22.6904"
                  stroke="white"
                  strokeWidth="10"
                  strokeLinecap="round"
                />
              </svg>

              {/* SPARKS */}
              <svg
                className="relative -top-28 sm:-top-32 md:-top-36 xl:-top-40 ml-auto translate-x-14"
                width="74"
                height="68"
                viewBox="0 0 74 68"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.1737 11.5L5.00122 42M68.1737 5L21.6737 49.5M37.6737 62.5L68.1737 49.5"
                  stroke="white"
                  strokeWidth="10"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {/* CIRCLE LOGO */}
            <div className="hidden md:block pr-2 transform -translate-y-14 lg:-translate-y-32">
              <EkatraCircleLogo size={120} />
            </div>
          </div>

          {/* PARAGRAPH */}
          {isEnglish ? (
  <p className="-mt-4 md:mt-8 text-[#033049] text-base sm:text-lg md:text-xl leading-relaxed max-w-none md:max-w-4xl pb-4">
    {t("dialogue.description.prefix")}{" "}
    <span className="font-bold">
      {t("dialogue.description.languages")}
    </span>{" "}
    {t("dialogue.description.suffix")}
  </p>
) : (
  <div className="-mt-4 md:mt-8 text-[#033049]">

    {/* INTRO LINE */}
    <p className="mb-8 text-base sm:text-lg md:text-xl">
      {t("dialogue.description.intro")}
    </p>

    {/* GRID */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 text-base sm:text-lg">

      {["individuals", "educators", "institutions", "society"].map((section) => (
  <div key={section}>
    <h4 className="font-bold mb-3">
      {t(`dialogue.description.sections.${section}.title`)}
    </h4>

    <ul className="space-y-2">
      <li>• {t(`dialogue.description.sections.${section}.point1`)}</li>
      <li>• {t(`dialogue.description.sections.${section}.point2`)}</li>
      <li>• {t(`dialogue.description.sections.${section}.point3`)}</li>
      <li>• {t(`dialogue.description.sections.${section}.point4`)}</li>
    </ul>
  </div>
))}

    </div>
  </div>
)}
        </div>
      </div>
    </section>
  );
}
